/**
 *
 *  CalendarDay
 *  TruBe Date Time CalendarDay Component
 *
 *  Created by Vladimir Shishlyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback } from "react";

// Components
import ProductBadge from "../../../TrainerCard/ProductBadge/ProductBadge";
import TrainerRating from "../../../TrainerRating/TrainerRating";
import ClampLines from "../../../ClampLines/ClampLines";

// Hookers
import useContentScrollerItem from "../../../../hooks/useContentScrollerItem/useContentScrollerItem";

// Utils
import { getPartnerPictureUrl } from "../../../../utils";

export default function TrainerCard({
  children,
  contentScrollerApi,
  trainer,
  isContentScrollerVisible,
  itemKey,
  onCardVisible,
  onCardVisibleChange,
  onCardClick,
  tabIndex,
}) {
  const cardRef = React.useRef(null);

  const handleCardClick = useCallback(
    (e) => {
      onCardClick && onCardClick(trainer, e);
    },
    [trainer, onCardClick]
  );

  useContentScrollerItem({
    contentScrollerApi: contentScrollerApi,
    element: cardRef.current,
    isScrollerVisible: isContentScrollerVisible,
    key: itemKey,
    onItemVisible: onCardVisible,
    onItemVisibleChange: onCardVisibleChange,
    threshold: 0.5,
  });

  const trainerProducts = trainer && trainer.products;
  const trainerHeadline = trainer && trainer.headline;
  const trainerRating = trainer && trainer.rating;
  const trainerCost = trainer && trainer.price && trainer.price.value;
  const priceSymbol = trainer && trainer.price && trainer.price.symbol;

  const numberOfRatings = 121;

  return (
    <div
      className="tbk-h-77_ tbk-flex tbk-h-full tbk-w-58 tbk-flex-col tbk-rounded-2xl tbk-border tbk-border-blue-grey-light tbk-shadow-trube"
      ref={cardRef}
      onClick={handleCardClick}
    >
      <div className="tbk-relative">
        <img
          className="tbk--mt-4_ tbk-h-54 tbk-w-full tbk-rounded-t-2xl tbk-object-cover"
          src={getPartnerPictureUrl(trainer)}
          alt={trainer.name}
        />
        <ul className="tb-disciplines-list tbk-absolute tbk-right-0 tbk-top-0 tbk-mt-1 tbk-mr-1 tbk-flex tbk-flex-col">
          {trainerProducts && trainerProducts.length
            ? trainerProducts
                .slice(0, 3)
                .map((prod) => (
                  <ProductBadge
                    key={prod.id}
                    showLabel={false}
                    className="tbk-mb-1 tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-basic-white tbk-text-primary"
                    iconSize={32}
                    workoutLabel={prod.name}
                  />
                ))
            : null}
        </ul>

        {trainerCost ? (
          <div className="tbk-absolute tbk-left-0 tbk-bottom-0 tbk-ml-1 tbk-mb-1 tbk-flex tbk-flex-col">
            <div
              className="tbk-text-main tbk-rounded-lg tbk-bg-basic-white tbk-text-primary tbk-shadow-trube"
              style={{
                padding: "2px 8px",
              }}
            >
              {priceSymbol || "$"}
              {trainerCost}
            </div>
          </div>
        ) : null}
      </div>

      <div className="tbk-px-2 tbk-pt-1 tbk-pb-2">
        <div className="tbk-flex tbk-items-center">
          <div className="tbk-text-title-bold tbk-text-primary">
            {trainer.firstName}
          </div>
          {numberOfRatings ? (
            <TrainerRating
              className="tbk-text-small-bold tbk-flex tbk-items-center tbk-justify-start"
              averageRatingClassName="tbk-text-primary tbk-text-main"
              numberOfSessionsClassName="tbk-text-grey tbk-text-small"
              partner={{
                partnerRatingRecap: {
                  average: trainerRating,
                  numberOfRatings: 0,
                },
              }}
            />
          ) : null}
        </div>
        <ClampLines
          text={trainerHeadline}
          id={"coach-headline"}
          className="tbk-text-small tbk-text-blue-grey"
          lines={3}
          ellipsis="..."
          buttons={false}
        />
      </div>
    </div>
  );
}
